import type { WireAgent } from '@shared/contract';
import { duration } from '@/lib/format';

/** Three missed pushes — the monitor's own staleness threshold. */
const STALE_AFTER_S = 180;
const VITAL_WARN_PCT = 85;

export function HostsView({ agents }: { agents: WireAgent[] }) {
  if (!agents.length) {
    return (
      <section className="view is-active" aria-label="Hosts">
        <div className="empty">
          <strong>No host agents have reported</strong>
          No agents are deployed in the current setup — this view stays empty unless one is
          added and <code>AGENT_TOKEN</code> is set to match the monitor.
        </div>
      </section>
    );
  }

  return (
    <section className="view is-active" aria-label="Hosts">
      {agents.map((agent) => (
        <section className="group" key={agent.hostKey}>
          <div className="group__head">
            <h2 className="group__title">{agent.hostKey}</h2>
            {agent.ageS > STALE_AFTER_S && <span className="group__badge">stale</span>}
            <span className="group__rule" />
            <span className="muted group__count">reported {duration(agent.ts)} ago</span>
          </div>
          <div className="panel">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Kind</th>
                    <th>Name</th>
                    <th>State</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {agent.processes.map((proc) => (
                    <tr key={`pm2-${proc.name}`}>
                      <td className="muted">pm2</td>
                      <td>
                        <code>{proc.name}</code>
                      </td>
                      <td className={proc.status === 'online' ? 'st-ok' : 'st-down'}>
                        {proc.status}
                      </td>
                      <td className="muted">
                        {proc.restarts} restart{proc.restarts === 1 ? '' : 's'}
                        {proc.memoryMb ? ` · ${Math.round(proc.memoryMb)} MB` : ''}
                      </td>
                    </tr>
                  ))}
                  {agent.containers.map((container) => (
                    <tr key={`docker-${container.name}`}>
                      <td className="muted">docker</td>
                      <td>
                        <code>{container.name}</code>
                      </td>
                      <td className={container.state === 'running' ? 'st-ok' : 'st-down'}>
                        {container.state}
                      </td>
                      <td className="muted">{container.health ?? 'no healthcheck'}</td>
                    </tr>
                  ))}
                  {Object.entries(agent.vitals).map(([key, value]) => (
                    <tr key={`vital-${key}`}>
                      <td className="muted">vital</td>
                      <td>
                        <code>{key}</code>
                      </td>
                      <td className={key.includes('pct') && value >= VITAL_WARN_PCT ? 'st-down' : ''}>
                        {value}
                        {key.includes('pct') ? '%' : ''}
                      </td>
                      <td className="muted" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ))}
    </section>
  );
}
